import { useEffect, useRef, type MutableRefObject } from "react";
import { prefersReducedMotion } from "./motion";

/** Short enough to feel like the page arrived, not like it is loading. */
const DURATION_MS = 180;
const EASE = "cubic-bezier(0.2, 0, 0, 1)";

/**
 * Fades content in when the thing it shows changes, without remounting it.
 *
 * The v2 shell swaps pages in place, so a route change used to replace one
 * screen with another between two frames. This animates the arrival instead.
 * It only ever animates *into* the natural state — the element is never left
 * hidden if the animation is skipped or interrupted — and it does nothing when
 * the reader asked for reduced motion.
 *
 * `selector` narrows what moves: the shell animates the page under its topbar,
 * so the bar itself stays put while the content beneath it changes.
 */
export function useEnterAnimation<T extends HTMLElement>(
  deps: ReadonlyArray<unknown>,
  selector?: string,
): MutableRefObject<T | null> {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const host = ref.current;
    if (!host || prefersReducedMotion()) return;
    const targets = selector
      ? Array.from(host.querySelectorAll<HTMLElement>(selector))
      : [host];
    for (const el of targets) {
      // Older engines (and jsdom) have no Web Animations API; the swap is then
      // instant, which is exactly the behaviour this replaces.
      if (typeof el.animate !== "function") continue;
      el.animate(
        [
          { opacity: 0, transform: "translateY(6px)" },
          { opacity: 1, transform: "none" },
        ],
        { duration: DURATION_MS, easing: EASE },
      );
    }
    // The caller decides what counts as a change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}
